import { useEffect, useState, useCallback } from "react";

import { fetchAvailableSlots } from "../../../api/calls/service-requests.call";
import { SlotPickerStyles } from "./slotPicker.styles";

const formatDateForApi = date => {
  if (!date) return null;
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
};

const formatSlotLabel = iso => {
  const d = new Date(iso);
  const h = d.getHours();
  const m = d.getMinutes();
  const ampm = h >= 12 ? "PM" : "AM";
  const displayHour = h % 12 === 0 ? 12 : h % 12;
  const mm = String(m).padStart(2, "0");
  return `${displayHour}:${mm} ${ampm}`;
};

const isSameSlot = (selectedTime, slotIso) => {
  if (!selectedTime) return false;
  const selected = new Date(selectedTime);
  const slot = new Date(slotIso);
  return selected.getTime() === slot.getTime();
};

const SlotPicker = ({
  serviceTypeId,
  excludeServiceId,
  selectedDate,
  selectedTime,
  onSelect
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);

  const loadSlots = useCallback(async () => {
    if (!serviceTypeId || !selectedDate) {
      setData(null);
      return;
    }
    const dateString = formatDateForApi(selectedDate);
    if (!dateString) return;

    setIsLoading(true);
    setError(null);
    const response = await fetchAvailableSlots({
      serviceTypeId,
      date: dateString,
      excludeServiceId
    });
    setIsLoading(false);

    if (response && response.hasError) {
      setError(response.error?.message || "Failed to load slots.");
      setData(null);
      return;
    }

    const body = response?.data || response;
    setData(body);
  }, [serviceTypeId, selectedDate, excludeServiceId]);

  useEffect(() => {
    loadSlots();
  }, [loadSlots]);

  const renderBody = () => {
    if (!serviceTypeId) {
      return (
        <div className="message">
          <p className="placeholder">Service type missing — slots unavailable.</p>
        </div>
      );
    }

    if (!selectedDate) {
      return (
        <div className="message">
          <p className="placeholder">Pick a date to view available slots.</p>
        </div>
      );
    }

    if (isLoading) {
      return (
        <div className="grid">
          {Array.from({ length: 18 }).map((_, i) => (
            <span key={i} className="shimmer" />
          ))}
        </div>
      );
    }

    if (error) {
      return (
        <div className="message">
          <p className="error">{error}</p>
        </div>
      );
    }

    if (!data) return null;

    if (data.closed) {
      return (
        <div className="message">
          <p className="closed">
            {data.closure_reason || "Branch is closed on this day."}
          </p>
        </div>
      );
    }

    if (!data.slots || data.slots.length === 0) {
      return (
        <div className="message">
          <p className="placeholder">No slots available for this day.</p>
        </div>
      );
    }

    return (
      <div className="grid">
        {data.slots.map(slot => {
          const selected = isSameSlot(selectedTime, slot.time);
          const cls = [
            "slot",
            slot.available ? "available" : "taken",
            selected ? "selected" : ""
          ]
            .filter(Boolean)
            .join(" ");
          return (
            <button
              key={slot.time}
              type="button"
              className={cls}
              disabled={!slot.available && !selected}
              onClick={() => slot.available && onSelect(new Date(slot.time))}
              title={
                slot.available
                  ? `${slot.booked}/${slot.capacity} booked${
                      slot.pending_count > 0
                        ? `, ${slot.pending_count} pending`
                        : ""
                    }`
                  : `Full (${slot.booked}/${slot.capacity})${
                      slot.pending_count > 0
                        ? `, ${slot.pending_count} pending`
                        : ""
                    }`
              }
            >
              {formatSlotLabel(slot.time)}
            </button>
          );
        })}
      </div>
    );
  };

  return (
    <SlotPickerStyles>
      <div className="legend">
        <span className="legendItem">
          <span className="dot available" /> Available
        </span>
        <span className="legendItem">
          <span className="dot taken" /> Taken
        </span>
      </div>
      <div className="body">{renderBody()}</div>
    </SlotPickerStyles>
  );
};

export default SlotPicker;
